import type {
  ActiveAgentSession,
  BootstrapConfig,
  ContactLinkedSummary,
  CrmListResponse,
  CrmRecord,
  LlmCheckResponse,
  TeamMember,
  TeamMemberListResponse,
} from "./types";

const SECRET_KEY = "agent-admin-secret";

export function getAdminSecret(): string {
  return localStorage.getItem(SECRET_KEY) ?? "";
}

export function setAdminSecret(secret: string): void {
  localStorage.setItem(SECRET_KEY, secret);
}

export function clearAdminSecret(): void {
  localStorage.removeItem(SECRET_KEY);
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Secret": getAdminSecret(),
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export const adminApi = {
  bootstrap: () => api<BootstrapConfig>("/api/bootstrap"),
  stats: () => api<{ stats: Record<string, number> }>("/api/crm/stats"),
  list: (entity: string, q = "") =>
    api<CrmListResponse>(`/api/crm/${entity}?limit=100${q ? `&q=${encodeURIComponent(q)}` : ""}`),
  listWithParams: (entity: string, params: Record<string, string | number | undefined> = {}) => {
    const search = new URLSearchParams({ limit: "100" });
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") search.set(key, String(value));
    }
    return api<CrmListResponse>(`/api/crm/${entity}?${search.toString()}`);
  },
  contactSummary: (id: number) =>
    api<ContactLinkedSummary>(`/api/crm/contacts/${id}/summary`),
  create: (entity: string, payload: Record<string, unknown>) =>
    api<{ record: CrmRecord }>(`/api/crm/${entity}`, { method: "POST", body: JSON.stringify(payload) }),
  update: (entity: string, id: number, payload: Record<string, unknown>) =>
    api<{ record: CrmRecord }>(`/api/crm/${entity}/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  delete: (entity: string, id: number) =>
    api<{ ok: true }>(`/api/crm/${entity}/${id}`, { method: "DELETE" }),
  listTeamMembers: (q = "") =>
    api<TeamMemberListResponse>(`/api/team-members?limit=200${q ? `&q=${encodeURIComponent(q)}` : ""}`),
  createTeamMember: (payload: Record<string, unknown>) =>
    api<{ member: TeamMember }>("/api/team-members", { method: "POST", body: JSON.stringify(payload) }),
  updateTeamMember: (id: number, payload: Record<string, unknown>) =>
    api<{ member: TeamMember }>(`/api/team-members/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteTeamMember: (id: number) =>
    api<{ ok: true }>(`/api/team-members/${id}`, { method: "DELETE" }),
  sessions: () => api<{ sessions: ActiveAgentSession[]; count: number }>("/admin/sessions"),
  errors: () => api<{ errors: Array<Record<string, unknown>> }>("/admin/errors"),
  llmCheck: (model?: string) =>
    api<LlmCheckResponse>("/admin/llm-check", {
      method: "POST",
      body: JSON.stringify(model ? { model } : {}),
    }),
  broadcast: (message: string) =>
    api<{ ok: boolean; attempted: number; failed: number }>("/admin/broadcast", {
      method: "POST",
      body: JSON.stringify({ message }),
    }),
};
